import { useRef, useState, type ReactNode, type PointerEvent as ReactPointerEvent } from "react"
import { Trash2 } from "lucide-react"

/** Fraction of the item's width the user must drag before releasing counts
 *  as a committed swipe. Below this, releasing snaps back with no effect. */
const REVEAL_THRESHOLD_RATIO = 0.35
/** Hard cap on how far the item can be dragged, in px — dragging further
 *  than the icon actually needs to reveal doesn't do anything useful, and
 *  an unbounded drag makes the snap-back animation feel worse. */
const MAX_DRAG_PX = 96

interface SwipeToDeleteProps {
  children: ReactNode
  /** Same `toast.confirmDestructive` callback every other destructive
   *  action in the app already goes through (design principle #3, see
   *  Toast.tsx) — crossing the threshold does NOT delete immediately, it
   *  triggers that same undoable confirmation. This component only owns
   *  the gesture, not the confirmation semantics. */
  onDelete: () => void
  /** Disables the gesture entirely (e.g. the viewer lacks permission to
   *  delete this particular row) while still rendering children normally. */
  disabled?: boolean
}

/**
 * iOS Mail/Gmail/Discord-style "drag the item to reveal a delete action
 * behind it" gesture. The item itself translates horizontally; it never
 * moves toward a trash can elsewhere on screen. Built on native Pointer
 * Events (not separate touch/mouse handlers) so the same code path
 * handles desktop and mobile.
 */
export function SwipeToDelete({ children, onDelete, disabled = false }: SwipeToDeleteProps) {
  const [dragX, setDragX] = useState(0)
  const [dragging, setDragging] = useState(false)
  const startX = useRef(0)
  const containerRef = useRef<HTMLDivElement>(null)

  function handlePointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (disabled) return
    startX.current = e.clientX
    setDragging(true)
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  function handlePointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (!dragging) return
    const delta = e.clientX - startX.current
    // Only leftward drags reveal the action (delete sits behind the item
    // on the right edge). Rightward movement is clamped to 0, not allowed
    // to push the item the other way.
    const clamped = Math.min(0, Math.max(-MAX_DRAG_PX, delta))
    setDragX(clamped)
  }

  function handlePointerUp() {
    if (!dragging) return
    setDragging(false)
    const width = containerRef.current?.offsetWidth ?? MAX_DRAG_PX
    const crossedThreshold = Math.abs(dragX) >= width * REVEAL_THRESHOLD_RATIO || Math.abs(dragX) >= MAX_DRAG_PX
    setDragX(0)
    if (crossedThreshold) onDelete()
  }

  return (
    <div ref={containerRef} style={{ position: "relative", overflow: "hidden", borderRadius: "var(--radius-md)" }}>
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          paddingRight: "var(--space-4)",
          background: "var(--status-error)",
          color: "var(--text-on-brand)",
        }}
      >
        <Trash2 size={18} />
      </div>
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          position: "relative",
          transform: `translateX(${dragX}px)`,
          transition: dragging ? "none" : "transform var(--duration-small) var(--ease-out)",
          touchAction: disabled ? "auto" : "pan-y",
          cursor: disabled ? "default" : "grab",
        }}
      >
        {children}
      </div>
    </div>
  )
}
